import React, { useRef, useState, useEffect } from 'react';

const Dropdown = ({ 
  id,
  className = '',
  value,
  onChange,
  options = [],
  disabled = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  children,
  ...restProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const listboxRef = useRef(null);

  // Add CSS for custom scrollbars and hover/focus effects
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-dropdown-menu::-webkit-scrollbar {
        width: 8px;
      }
      .custom-dropdown-menu::-webkit-scrollbar-track {
        background: rgba(65, 65, 65, 0.1);
        border-radius: 4px;
      }
      .custom-dropdown-menu::-webkit-scrollbar-thumb {
        background: #7D3F98;
        border-radius: 4px;
        transition: background 0.2s ease;
      }
      .custom-dropdown-menu::-webkit-scrollbar-thumb:hover {
        background: #8932AF;
      }
      .custom-dropdown-menu {
        scrollbar-width: thin;
        scrollbar-color: #7D3F98 rgba(65, 65, 65, 0.1);
      }
      .dropdown-button:hover:not(:disabled) {
        box-shadow: 0 0 0 3px rgba(128, 128, 128, 0.3);
      }
      .dropdown-button:focus:not(:disabled) {
        box-shadow: 0 0 0 3px rgba(125, 63, 152, 0.3);
        outline: none;
      }
      .dropdown-button:focus:hover:not(:disabled) {
        box-shadow: 0 0 0 3px rgba(125, 63, 152, 0.3);
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Get all options as a consistent array
  const getAllOptions = () => {
    if (children) {
      return React.Children.toArray(children)
        .filter(child => child.type === 'option')
        .map(child => ({
          value: child.props.value,
          label: child.props.children || child.props.value
        }));
    }
    return options;
  };

  const allOptions = getAllOptions();
  const selectedOption = allOptions.find(option => option.value === value);
  const displayText = selectedOption ? selectedOption.label : (allOptions[0]?.label || 'Select...');

  // Check if component has error state
  const hasError = className.includes('error');

  // Scroll highlighted option into view and announce it
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listboxRef.current) {
      const highlightedElement = listboxRef.current.querySelector(`#${id}-option-${highlightedIndex}`);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        
        // Announce the highlighted option to screen readers
        const option = allOptions[highlightedIndex];
        if (option) {
          announceToScreenReader(`${option.label}`);
        }
      }
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectOption = (option) => {
    if (onChange && option) {
      const syntheticEvent = {
        target: { value: option.value },
        currentTarget: { value: option.value },
        type: 'change'
      };
      onChange(syntheticEvent);
      
      // Announce the selection to screen readers
      announceToScreenReader(`${option.label} selected`);
    }
    setIsOpen(false);
    setHighlightedIndex(-1);
    buttonRef.current?.focus();
  };

  // Function to announce changes to screen readers
  const announceToScreenReader = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    
    document.body.appendChild(announcement);
    
    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case ' ':
      case 'Enter':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          const selectedIndex = allOptions.findIndex(option => option.value === value);
          setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        } else if (highlightedIndex >= 0) {
          selectOption(allOptions[highlightedIndex]);
        }
        break;
        
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          const selectedIndex = allOptions.findIndex(option => option.value === value);
          setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        } else {
          setHighlightedIndex(prev => 
            prev < allOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          const selectedIndex = allOptions.findIndex(option => option.value === value);
          setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : allOptions.length - 1);
        } else {
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : allOptions.length - 1
          );
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        buttonRef.current?.focus();
        break;
        
      case 'Tab':
        if (isOpen) {
          setIsOpen(false);
          setHighlightedIndex(-1);
        }
        break;
    }
  };

  const handleButtonClick = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      const selectedIndex = allOptions.findIndex(option => option.value === value);
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  };

    return (
    <div 
      className={className}
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
      style={{ 
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        // Reset any external styling that might be applied
        padding: 0,
        border: 'none',
        borderRadius: 0,
        background: 'transparent',
        backgroundImage: 'none',
        backgroundRepeat: 'initial',
        backgroundPosition: 'initial',
        backgroundSize: 'initial',
        cursor: 'auto',
        appearance: 'initial',
        WebkitAppearance: 'initial',
        MozAppearance: 'initial'
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        id={id}
        className="dropdown-button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? `${id}-listbox` : undefined}
        aria-activedescendant={isOpen && highlightedIndex >= 0 ? `${id}-option-${highlightedIndex}` : undefined}
        aria-label={ariaLabel || `${displayText}, dropdown`}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onClick={handleButtonClick}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: className.includes('sort-dropdown') 
            ? '12px 40px 12px 0' 
            : className.includes('filter-dropdown') 
              ? '12px 50px 12px 12px' 
              : '12px 40px 12px 12px',
          borderRadius: '4px',
          border: hasError ? '2px solid var(--Error, #E10506)' : '2px solid var(--Text-black, #262626)',
          background: 'var(--White, #FFF)',
          fontFamily: '"CVS Health Sans"',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '25.6px',
          cursor: 'pointer',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none',
          backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%237D3F98' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 12px center',
          backgroundSize: '20px', // Increased from 16px to match Figma
          paddingRight: className.includes('filter-dropdown') ? '50px' : '40px', // Extra padding only for filter dropdowns
          color: disabled ? '#666' : 'inherit',
          outline: 'none',
          boxSizing: 'border-box',
          margin: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
        {...restProps}
      >
        {displayText}
      </button>
      
      {isOpen && (
        <ul 
          ref={listboxRef}
          role="listbox"
          id={`${id}-listbox`}
          aria-label={ariaLabel || "Select an option"}
          className="custom-dropdown-menu"
          style={{
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            position: 'absolute',
            top: '100%',
            left: '0',
            right: '0',
            zIndex: 1000,
            backgroundColor: 'white',
            margin: 0,
            padding: 0,
            listStyle: 'none',
            boxSizing: 'border-box'
          }}
        >
          {allOptions.map((option, index) => (
            <li 
              key={option.value || index} 
              role="option"
              id={`${id}-option-${index}`}
              aria-selected={highlightedIndex === index}
              style={{ margin: 0, padding: 0 }}
            >
              <button
                type="button"
                tabIndex={-1}
                onClick={() => selectOption(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                aria-label={option.label}
                style={{
                  padding: '8px 12px',
                  border: 'none',
                  background: highlightedIndex === index ? '#7D3F98' : 'transparent',
                  color: highlightedIndex === index ? 'white' : 'inherit',
                  width: '100%',
                  textAlign: 'left',
                  fontSize: '16px',
                  fontFamily: '"CVS Health Sans"',
                  cursor: 'pointer',
                  outline: 'none',
                  boxSizing: 'border-box',
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      
      {/* Hidden select for form compatibility */}
      <select
        style={{ display: 'none' }}
        value={value}
        onChange={() => {}} // Controlled by the dropdown above
        tabIndex={-1}
        aria-hidden="true"
      >
        {children || options.map((option, index) => (
          <option key={option.value || index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default Dropdown;